// Invite-accept seam. The link in the invitation email lands on /invite/:token;
// the page collects a name + password and calls this to create the account.
//   acceptInvite -> POST /auth/invite/accept { token, name, password }
// On success the account exists and the person can sign in. A 400 means the
// link is no longer valid (used / expired / tampered).

import { isAxiosError } from 'axios'

import { apiClient } from '../../api/client.js'

export type AcceptResult = 'ok' | 'invalid' | 'weak_password'

// Read-only precheck the accept page runs on load so a dead link (used /
// expired) shows the "ya no sirve" state immediately. A network/server error
// returns true so we still render the form rather than wrongly blocking a
// valid link; the real accept call is the authoritative gate.
export async function checkInvite(token: string): Promise<boolean> {
  try {
    const res = await apiClient.get('/auth/invite/check', { params: { token } })
    return (res.data as { data?: { valid?: boolean } } | undefined)?.data?.valid !== false
  } catch {
    return true
  }
}

/**
 * POST /auth/invite/accept — creates the account from the emailed token.
 * Maps a 400 to 'weak_password' or 'invalid' (used/expired/tampered link);
 * other errors rethrow.
 */
export async function acceptInvite(
  token: string,
  name: string,
  password: string,
): Promise<AcceptResult> {
  try {
    await apiClient.post('/auth/invite/accept', { token, name, password })
    return 'ok'
  } catch (err) {
    if (isAxiosError(err) && err.response?.status === 400) {
      const code = (err.response.data as { error?: { code?: string } } | undefined)?.error?.code
      if (code === 'weak_password') return 'weak_password'
      return 'invalid'
    }
    throw err
  }
}
